import * as React from 'react';
import type {ReactNode} from 'react';
import {ComboboxTrigger} from '../ComboboxTrigger';
import {DefaultMultiValueChip} from './DefaultMultiValueChip';
import type {ControlProps} from './types';
import type {SelectValue} from '../types';

function DefaultControl<T, V extends SelectValue, L extends ReactNode>(
  props: ControlProps<T, V, L>,
) {
  const {
    triggerId,
    listboxId,
    labelId,
    valueId,
    describedErrorId,
    accessibleLabel,
    getOptionId,
    inputRef,
    inputValue,
    placeholder,
    showPlaceholderWhenIdle,
    isMulti,
    isOpen,
    isFocused,
    isBlocked,
    hasValue,
    isEmpty,
    invalid,
    loading,
    disabled,
    readOnly,
    highlightedIndex,
    selectedOptions,
    onInputChange,
    onInputFocus,
    onInputKeyDown,
    onContainerClick,
    onClear,
    onRemoveOption,
    clearable,
    clearLabel,
    hideIndicator,
    autoFocus,
    leftIcon,
    searchable,
    components,
  } = props;

  const Chip = components.MultiValueChip ?? DefaultMultiValueChip;
  const showClear = Boolean(clearable) && hasValue && !readOnly && !disabled;

  const placeholderText = isMulti ? (!hasValue ? placeholder : '') : placeholder;

  return (
    <ComboboxTrigger
      triggerId={triggerId}
      listboxId={listboxId}
      labelId={labelId}
      valueId={valueId}
      describedErrorId={describedErrorId}
      accessibleLabel={accessibleLabel}
      isOpen={isOpen}
      isFocused={isFocused}
      isBlocked={isBlocked}
      hasValue={hasValue}
      isEmpty={isEmpty}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      invalid={invalid}
      isMulti={isMulti}
      inputRef={inputRef}
      inputValue={inputValue}
      placeholder={placeholderText}
      showPlaceholderWhenIdle={showPlaceholderWhenIdle}
      highlightedIndex={highlightedIndex}
      getOptionId={getOptionId}
      onInputChange={onInputChange}
      onInputFocus={onInputFocus}
      onInputKeyDown={onInputKeyDown}
      onContainerClick={onContainerClick}
      showClear={showClear}
      onClear={onClear}
      clearLabel={clearLabel}
      hideIndicator={hideIndicator}
      autoFocus={autoFocus}
      leftIcon={leftIcon}
      searchable={searchable}
      leadingContent={
        isMulti
          ? selectedOptions.map(option => (
              <Chip
                key={String(option.value)}
                option={option}
                readOnly={readOnly}
                onRemove={() => onRemoveOption(option)}
              />
            ))
          : undefined
      }
    />
  );
}

export {DefaultControl};
